// The art for Business setup's sections (practice/app/onboarding.jsx, the
// Monzo-style section hub and the intro screen in front of each section).
//
// Two sets, in the house style of shared/howItWorksArt.js: flat, rounded, no
// outlines, each object lifted off the page by a `depth` layer.
//   HEROES  160 by 120, free standing (no disc behind them), big on an intro
//   CARDS   72 by 72, the small picture on the left of a section card
//
// ⛔ Colours are {{slot}}s filled from the pastel tones only (fillIcon in
// shared/announcementIcons.js): never turquoise (the logo) or the link blue
// (actions). Decorative: the screen carries the words.
//
// Kept as XML rather than JSX so the same source renders in the app (SvgXml) and
// to PNG for review (practice/scripts/preview-section-art.mjs), and cannot drift.

import { dot, line, lift, path, rect, sparkle } from './howItWorksArt.js'

const card = (x, y, w, h, r) => (f) => rect(x, y, w, h, r, f)
const turn = (deg, cx, cy, body) => `<g transform="rotate(${deg} ${cx} ${cy})">${body}</g>`

// The shopfront (yellow front, striped awning), shared by the business hero and
// the verified one.
const SHOP =
  lift(card(42, 44, 76, 60, 5), '{{yellow}}', 4, 4)
    + rect(50, 62, 22, 16, 3, '{{white}}')
    + path('M84 104V68a4 4 0 0 1 4-4h14a4 4 0 0 1 4 4v36z')('{{mint}}')
    + dot(100, 86, 1.8, '{{ink}}')
    + rect(87, 72, 16, 8, 2, '{{white}}') + rect(90, 75, 10, 2, 1, '{{pink}}')
    + `<g transform="translate(4 4)"><path d="M36 48 46 28h68l10 20z" fill="{{depth}}"/></g>`
    + path('M46 28h13.6L53.6 48H36z')('{{coral}}') + path('M59.6 28h13.6l-2 20H53.6z')('{{softPink}}')
    + path('M73.2 28h13.6l2 20H71.2z')('{{coral}}') + path('M86.8 28h13.6l6 20H88.8z')('{{softPink}}')
    + path('M100.4 28H114l10 20h-17.6z')('{{coral}}')
    + dot(44.8, 48, 8.8, '{{coral}}') + dot(62.4, 48, 8.8, '{{softPink}}') + dot(80, 48, 8.8, '{{coral}}')
    + dot(97.6, 48, 8.8, '{{softPink}}') + dot(115.2, 48, 8.8, '{{coral}}')

// A map pin: a round head and a point, lifted like everything else.
const pin = (cx, cy, r, fill) =>
  lift((f) => dot(cx, cy, r, f) + path(`M${cx - r * 0.82} ${cy + r * 0.58}L${cx} ${cy + r * 1.9}L${cx + r * 0.82} ${cy + r * 0.58}Z`)(f), fill, 2, 2)
  + dot(cx, cy, r * 0.42, '{{white}}')

// A small shopfront for the locations hero: a front, a window, a door and a
// scalloped awning in two alternating colours (the business shop's, smaller).
const smallShop = (x, y, w, h, front, stripeA, stripeB, door) => {
  const aw = Math.round(h * 0.32)
  const n = 4
  const top = y - aw
  const l = x - 5
  const r = x + w + 5
  const sw = (r - l) / n
  const inset = 4
  const xt = (i) => x + (w * i) / n
  let awning = `<g transform="translate(3 3)"><path d="M${x} ${top}H${x + w}L${r} ${y}H${l}Z" fill="{{depth}}"/></g>`
  for (let i = 0; i < n; i += 1) {
    const f = i % 2 ? stripeB : stripeA
    awning += path(`M${xt(i)} ${top}H${xt(i + 1)}L${l + sw * (i + 1)} ${y}H${l + sw * i}Z`)(f)
  }
  for (let i = 0; i < n; i += 1) awning += dot(l + sw * (i + 0.5), y, sw / 2, i % 2 ? stripeB : stripeA)
  return lift(card(x, y - inset, w, h + inset, 4), front, 3, 3)
    + rect(x + w * 0.14, y + h * 0.3, w * 0.34, h * 0.26, 2.5, '{{white}}')
    + rect(x + w * 0.58, y + h * 0.4, w * 0.28, h * 0.6, 2.5, door)
    + awning
}

const HEROES = {
  // The front door: three cards fanned like a wallet, the front one a shift
  // that is covered (a clock and a tick). What the app is for, before any form.
  frontDoor:
    turn(-22, 80, 64, lift(card(38, 38, 84, 52, 8), '{{lavender}}', 4, 4) + rect(98, 46, 16, 6, 3, '{{white}}'))
    + turn(-12, 80, 64, lift(card(38, 38, 84, 52, 8), '{{white}}', 4, 4) + rect(98, 46, 16, 6, 3, '{{softPink}}'))
    + turn(-3, 80, 64,
      lift(card(38, 38, 84, 52, 8), '{{coral}}', 4, 4)
      + dot(56, 56, 9, '{{mint}}') + line('M56 51v5l3.5 2.5', '{{ink}}', 2.2)
      + rect(70, 50, 30, 5, 2.5, '{{white}}') + rect(70, 60, 20, 5, 2.5, '{{white}}', 0.7)
      + dot(108, 78, 7, '{{lime}}') + line('M104.5 78l2.5 2.5 4.5-5', '{{ink}}', 2))
    + sparkle(134, 24, 8, '{{yellow}}') + sparkle(26, 98, 6, '{{pink}}') + sparkle(140, 96, 4, '{{mint}}'),

  // Your business: a shop with its awning out and a lens over it. We find the
  // business on the register so they do not have to type it.
  business:
    SHOP
    + line('M134 96l12 12', '{{depth}}', 8) + line('M132 94l12 12', '{{lavender}}', 8)
    + `<circle cx="126" cy="88" r="13" fill="none" stroke="{{depth}}" stroke-width="6"/>`
    + `<circle cx="124" cy="86" r="13" fill="{{white}}" fill-opacity="0.75" stroke="{{lavender}}" stroke-width="6"/>`
    + sparkle(26, 30, 8, '{{yellow}}') + sparkle(142, 20, 5, '{{pink}}') + sparkle(22, 92, 4, '{{lavender}}'),

  // Your locations: three shopfronts on one street, a pin over each. One
  // business, several places professionals come to.
  locations:
    lift(card(8, 100, 144, 8, 4), '{{lavender}}', 3, 3)
    + smallShop(18, 68, 34, 32, '{{coral}}', '{{yellow}}', '{{lime}}', '{{white}}')
    + smallShop(64, 58, 40, 42, '{{yellow}}', '{{coral}}', '{{softPink}}', '{{mint}}')
    + smallShop(116, 72, 30, 28, '{{mint}}', '{{pink}}', '{{softPink}}', '{{yellow}}')
    + pin(35, 32, 8, '{{pink}}') + pin(84, 18, 9, '{{coral}}') + pin(131, 40, 7, '{{lavender}}')
    + sparkle(14, 20, 6, '{{yellow}}') + sparkle(150, 16, 5, '{{mint}}') + sparkle(112, 30, 3.5, '{{pink}}'),

  // Signing for the business: a page, a pen finishing a signature, a seal.
  signing:
    lift(card(44, 16, 64, 88, 6), '{{white}}', 4, 4)
    + rect(54, 30, 40, 4, 2, '{{lavender}}') + rect(54, 40, 32, 4, 2, '{{lavender}}', 0.6)
    + rect(54, 50, 44, 4, 2, '{{lavender}}', 0.6) + rect(54, 60, 26, 4, 2, '{{lavender}}', 0.6)
    + line('M54 84c5-9 9-9 9-3s-2 7 3 1 6-8 8-2 4 3 8-1', '{{coral}}', 3)
    + rect(54, 90, 34, 2, 1, '{{lavender}}')
    + lift((f) => dot(100, 92, 12, f), '{{yellow}}', 3, 3) + sparkle(100, 92, 6, '{{coral}}')
    + turn(38, 104, 54,
      lift(card(98, 26, 12, 42, 6), '{{pink}}', 3, 3)
      + rect(98, 34, 12, 5, 0, '{{yellow}}')
      + path('M99 66h10l-5 12z')('{{ink}}'))
    + sparkle(28, 30, 8, '{{yellow}}') + sparkle(138, 22, 5, '{{mint}}') + sparkle(132, 104, 5, '{{pink}}'),

  // Paying for shifts: a card ready, a coin, a calendar with a day ticked. You
  // pay for a shift once it is done.
  paying:
    turn(-12, 72, 54, lift(card(34, 30, 76, 48, 7), '{{mint}}', 4, 4) + rect(42, 66, 24, 5, 2.5, '{{white}}'))
    + turn(4, 84, 68,
      lift(card(46, 44, 76, 48, 7), '{{coral}}', 4, 4)
      + rect(56, 54, 14, 11, 2.5, '{{yellow}}')
      + rect(56, 78, 22, 5, 2.5, '{{white}}') + rect(82, 78, 10, 5, 2.5, '{{white}}', 0.7))
    + lift((f) => dot(122, 94, 16, f), '{{lime}}', 3, 3)
    + line('M127 87c-1-2-3-3-5-3-3 0-5 2-5 5v12m-3-6h9m-9 6h13', '{{ink}}', 2.6)
    + lift(card(14, 76, 30, 30, 5), '{{white}}', 3, 3) + rect(14, 76, 30, 9, 4, '{{pink}}')
    + line('M22 95l4 4 8-8', '{{ink}}', 2.6)
    + sparkle(132, 26, 8, '{{yellow}}') + sparkle(24, 40, 5, '{{lavender}}') + sparkle(146, 62, 4, '{{pink}}'),

  // ── The rest of setup (founder 2026-10-02: the same art throughout) ────────

  // Create account, email: a letter rising out of an open envelope.
  email:
    lift(card(36, 44, 88, 56, 8), '{{lavender}}', 4, 4)
    + lift(card(50, 16, 60, 58, 6), '{{white}}', 3, 3)
    + rect(60, 28, 32, 5, 2.5, '{{pink}}') + rect(60, 39, 40, 4, 2, '{{lavender}}', 0.6) + rect(60, 48, 26, 4, 2, '{{lavender}}', 0.6)
    + path('M36 58l44 24 44-24v34a8 8 0 0 1-8 8H44a8 8 0 0 1-8-8z')('{{lavender}}')
    + line('M40 96l28-20M120 96l-28-20', '{{white}}', 3)
    + lift((f) => dot(122, 46, 10, f), '{{coral}}', 2, 2) + dot(122, 46, 3.5, '{{white}}')
    + sparkle(28, 26, 8, '{{yellow}}') + sparkle(140, 18, 5, '{{mint}}') + sparkle(140, 102, 5, '{{pink}}'),

  // Create account, code: the six digits landing in an open envelope (the code
  // comes by email). Mint, so it reads apart from the lavender email hero.
  code:
    lift(card(30, 52, 100, 56, 8), '{{mint}}', 4, 4)
    + path('M30 60 80 22l50 38z')('{{mint}}') + '<path d="M40 60 80 30l40 30z" fill="{{white}}" fill-opacity="0.35"/>'
    + turn(-5, 80, 58,
      lift(card(34, 44, 92, 30, 9), '{{yellow}}', 3, 3)
      + [0, 1, 2, 3, 4, 5].map((i) => rect(41 + i * 13.4, 49, 10, 20, 3, '{{white}}') + dot(46 + i * 13.4, 59, 2.6, '{{ink}}')).join(''))
    + path('M30 66l50 26 50-26v34a8 8 0 0 1-8 8H38a8 8 0 0 1-8-8z')('{{mint}}')
    + line('M34 104l30-20M126 104l-30-20', '{{white}}', 3)
    + line('M80 4v8M68 8l3 5M92 8l-3 5', '{{coral}}', 2.6)
    + sparkle(138, 30, 8, '{{yellow}}') + sparkle(22, 36, 5, '{{pink}}') + sparkle(144, 100, 5, '{{lavender}}'),

  // Create account, password: a padlock and a row of hidden characters.
  password:
    line('M62 56V40a18 18 0 0 1 36 0v16', '{{depth}}', 10).replace('<path', '<path transform="translate(3 3)"')
    + line('M62 56V40a18 18 0 0 1 36 0v16', '{{lavender}}', 10)
    + lift(card(44, 52, 72, 54, 10), '{{coral}}', 4, 4)
    + dot(80, 74, 7, '{{ink}}') + rect(77.5, 76, 5, 14, 2.5, '{{ink}}')
    + lift(card(96, 88, 54, 22, 11), '{{mint}}', 3, 3)
    + [0, 1, 2, 3].map((i) => dot(108 + i * 10, 99, 3, '{{white}}')).join('')
    + sparkle(30, 30, 8, '{{yellow}}') + sparkle(130, 26, 5, '{{pink}}') + sparkle(22, 96, 5, '{{lavender}}'),

  // Joining a business: two people's cards and a key passing between them.
  handover:
    turn(-8, 46, 60, lift(card(16, 36, 60, 46, 8), '{{mint}}', 4, 4) + dot(32, 52, 7, '{{white}}') + rect(26, 66, 32, 5, 2.5, '{{white}}', 0.8))
    + turn(8, 114, 60, lift(card(84, 36, 60, 46, 8), '{{coral}}', 4, 4) + dot(128, 52, 7, '{{white}}') + rect(102, 66, 32, 5, 2.5, '{{white}}', 0.8))
    + line('M84 94h30M104 94v8M112 94v6', '{{depth}}', 6).replace('<path', '<path transform="translate(2 2)"')
    + line('M84 94h30M104 94v8M112 94v6', '{{yellow}}', 6)
    + lift((f) => dot(76, 94, 12, f), '{{yellow}}', 2, 2) + dot(76, 94, 4.5, '{{white}}')
    + sparkle(80, 18, 8, '{{lavender}}') + sparkle(24, 104, 5, '{{pink}}') + sparkle(146, 102, 5, '{{lime}}'),

  // Final checks: a lens with an eye looking over the business's papers.
  checking:
    lift(card(26, 14, 66, 90, 6), '{{white}}', 4, 4)
    + rect(36, 26, 36, 5, 2.5, '{{coral}}') + rect(36, 37, 46, 4, 2, '{{lavender}}', 0.6)
    + rect(36, 46, 40, 4, 2, '{{lavender}}', 0.6) + rect(36, 55, 30, 4, 2, '{{lavender}}', 0.6)
    + lift(card(36, 72, 22, 20, 4), '{{yellow}}', 2, 2) + rect(36, 72, 22, 6, 3, '{{softPink}}')
    + line('M128 86l16 16', '{{depth}}', 11).replace('<path', '<path transform="translate(3 3)"')
    + line('M128 86l16 16', '{{coral}}', 11)
    + `<circle cx="109" cy="65" r="27" fill="none" stroke="{{depth}}" stroke-width="9"/>`
    + `<circle cx="106" cy="62" r="27" fill="{{white}}" stroke="{{lavender}}" stroke-width="9"/>`
    + `<circle cx="106" cy="62" r="21.5" fill="none" stroke="{{mint}}" stroke-width="3"/>`
    + path('M88 62q18-17 36 0q-18 17-36 0z')('{{mint}}')
    + dot(106, 62, 7.5, '{{white}}') + dot(106, 62, 4.5, '{{ink}}') + dot(108, 60, 1.6, '{{white}}')
    + sparkle(142, 26, 8, '{{yellow}}') + sparkle(20, 30, 5, '{{pink}}') + sparkle(16, 98, 4, '{{mint}}'),

  // Final checks cleared: the shopfront with a tick.
  verified:
    SHOP
    + lift((f) => dot(126, 90, 17, f), '{{lime}}', 3, 3)
    + line('M118 90l6 6 10-11', '{{ink}}', 3.4)
    + sparkle(26, 30, 8, '{{yellow}}') + sparkle(142, 20, 5, '{{pink}}') + sparkle(22, 92, 4, '{{lavender}}'),
  // Welcome (Today's first card for a new business): the shopfront with its
  // door swung open, a mat out front, bunting strung over the awning and
  // confetti in the air.
  welcome:
    SHOP
    + path('M84 104V68a4 4 0 0 1 4-4h14a4 4 0 0 1 4 4v36z')('{{lavender}}')
    + lift(path('M106 66L117 71V104L106 104Z'), '{{mint}}', 2, 2) + dot(114, 87, 1.6, '{{ink}}')
    + lift(card(78, 104, 34, 6, 3), '{{coral}}', 2, 2)
    + line('M30 6Q80 18 130 6', '{{lavender}}', 2)
    + [['{{pink}}', 40], ['{{yellow}}', 55], ['{{mint}}', 70], ['{{coral}}', 85], ['{{lime}}', 100], ['{{softPink}}', 115]]
      .map(([f, x]) => {
        const t = (x - 30) / 100
        const y = 6 + 24 * t * (1 - t)
        return path(`M${x - 5} ${y.toFixed(1)}h10l-5 9z`)(f)
      }).join('')
    + turn(24, 20, 44, rect(16, 42, 8, 4, 2, '{{pink}}')) + turn(-30, 142, 40, rect(138, 38, 8, 4, 2, '{{yellow}}'))
    + turn(40, 18, 76, rect(14, 74, 8, 4, 2, '{{mint}}')) + turn(-18, 144, 70, rect(140, 68, 8, 4, 2, '{{coral}}'))
    + dot(28, 60, 2.4, '{{lavender}}') + dot(136, 56, 2.4, '{{lime}}') + dot(150, 90, 2, '{{pink}}')
    + sparkle(22, 24, 7, '{{yellow}}') + sparkle(142, 22, 5, '{{pink}}') + sparkle(22, 100, 5, '{{lavender}}') + sparkle(138, 104, 4, '{{mint}}'),
}

const CARDS = {
  // Your account: an envelope with a key tucked behind it.
  account:
    dot(52, 20, 9, '{{depth}}') + line('M55 25l11 11', '{{depth}}', 5)
    + dot(50, 18, 9, '{{yellow}}') + dot(50, 18, 3.4, '{{white}}')
    + line('M53 23l11 11M60 30l-3 3', '{{yellow}}', 5)
    + lift(card(8, 28, 48, 32, 5), '{{lavender}}')
    + line('M12 32l20 14 20-14', '{{white}}', 3)
    + sparkle(14, 14, 5, '{{pink}}'),

  // Your business: a small shop with its awning and a tick.
  business:
    lift(card(12, 30, 42, 30, 4), '{{yellow}}')
    + rect(17, 38, 11, 9, 2, '{{white}}') + rect(34, 40, 11, 20, 2.5, '{{mint}}')
    + lift(path('M8 31 14 18h38l6 13z'), '{{coral}}', 2, 2)
    + path('M22.7 18h8.6l-1.4 13H19z')('{{softPink}}') + path('M39.3 18h8.6l4.6 13h-9z')('{{softPink}}')
    + lift((f) => dot(57, 55, 10, f), '{{lime}}', 2, 2) + line('M52.5 55l3 3 6-6', '{{ink}}', 2.4),

  // Signing for the business: two overlapping cards with signatures on them.
  signing:
    lift(card(8, 14, 40, 28, 5), '{{mint}}')
    + line('M15 30c3-6 6-6 6-2s-1 5 2 1 4-5 5-1 3 2 5-1', '{{white}}', 2.6)
    + lift(card(24, 32, 40, 28, 5), '{{coral}}')
    + line('M31 48c3-6 6-6 6-2s-1 5 2 1 4-5 5-1 3 2 5-1', '{{white}}', 2.6)
    + sparkle(60, 14, 6, '{{yellow}}'),

  // Paying for shifts: one card, tilted, with a sparkle.
  paying:
    turn(-14, 36, 40,
      lift(card(12, 25, 48, 30, 5), '{{coral}}')
      + rect(18, 31, 9, 7, 2, '{{yellow}}') + rect(18, 45, 16, 4, 2, '{{white}}'))
    + sparkle(58, 14, 7, '{{mint}}') + sparkle(14, 60, 4, '{{lavender}}'),

  // The invite card on the sign in screen: a ticket in an envelope.
  invite:
    turn(-10, 36, 30, lift(card(18, 10, 36, 26, 4), '{{pink}}', 2, 2) + dot(36, 23, 4, '{{white}}'))
    + lift(card(10, 30, 52, 30, 5), '{{lavender}}')
    + line('M14 34l22 14 22-14', '{{white}}', 3)
    + sparkle(62, 16, 5, '{{yellow}}'),
}

const svg = (w, h, body) => `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}">${body}</svg>`

export const SECTION_HERO_XML = Object.fromEntries(Object.entries(HEROES).map(([k, b]) => [k, svg(160, 120, b)]))
export const SECTION_CARD_XML = Object.fromEntries(Object.entries(CARDS).map(([k, b]) => [k, svg(72, 72, b)]))
